/**
 * Read in-progress chart config from localStorage.
 * Prefer newsavedChartData (live create/edit session) over savedChartData (last persisted plot).
 */
export function getStoredChartData() {
  try {
    const newest = localStorage.getItem("newsavedChartData");
    if (newest) {
      const parsed = JSON.parse(newest);
      if (parsed) return parsed;
    }
    const saved = localStorage.getItem("savedChartData");
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

/**
 * Normalize API samples payload to row array.
 */
export function parseTableSamples(samples) {
  if (samples == null) return [];
  if (Array.isArray(samples)) return samples;
  if (typeof samples === "object" && samples.rows) return samples.rows;
  return [];
}

/**
 * True when an axios/fetch error is from request cancellation (not a real failure).
 */
export function isRequestAborted(error) {
  if (!error) return false;
  if (error.name === "AbortError" || error.name === "CanceledError") return true;
  if (error.code === "ERR_CANCELED" || error.code === "ECONNABORTED") return true;
  return false;
}

/**
 * Normalize Y-axis keys from chart metadata (API may return YAxisColumn string or YAxisColumns array).
 */
export function parseYAxisKeysFromChart(chart, options = {}) {
  const excludeGroup = options.excludeGroup !== false;
  const groupColumn = chart?.GroupColumn || options.groupColumn || "";

  let keys = [];
  if (chart?.YAxisColumn) {
    if (Array.isArray(chart.YAxisColumn)) {
      keys = chart.YAxisColumn.map((col) => String(col).trim()).filter(Boolean);
    } else if (typeof chart.YAxisColumn === "string") {
      keys = chart.YAxisColumn.split(",").map((col) => col.trim()).filter(Boolean);
    }
  } else if (Array.isArray(chart?.YAxisColumns)) {
    keys = chart.YAxisColumns.map((col) => String(col).trim()).filter(Boolean);
  }

  if (excludeGroup && groupColumn) {
    keys = keys.filter((col) => col !== groupColumn);
  }

  return keys;
}

/**
 * Build YAxisColumns array for API (Y keys + legend column at end, same as Studio save).
 */
export function buildYAxisColumnsArray(yAxisKeys, groupColumn) {
  const keys = (Array.isArray(yAxisKeys) ? yAxisKeys : [])
    .map((col) => String(col).trim())
    .filter(Boolean)
    .filter((col) => col !== groupColumn);

  const result = [...keys];
  if (groupColumn && !result.includes(groupColumn)) {
    result.push(groupColumn);
  }
  return result;
}

/**
 * Comma-separated YAxisColumn string without legend column.
 */
export function buildYAxisColumnString(yAxisKeys, groupColumn) {
  return buildYAxisColumnsArray(yAxisKeys, groupColumn)
    .filter((col) => col !== groupColumn)
    .join(",");
}

/**
 * Collect unique column names needed for table data fetch.
 */
export function buildSelectColumnsFromState({
  xAxisKey,
  yAxisKeys,
  zAxisKey,
  groupColumn,
  chartFallback,
}) {
  const columns = new Set();

  const add = (col) => {
    if (col && typeof col === "string" && col.trim()) {
      columns.add(col.trim());
    }
  };

  add(xAxisKey);
  add(zAxisKey);
  add(groupColumn);

  const yKeys = Array.isArray(yAxisKeys)
    ? yAxisKeys
    : parseYAxisKeysFromChart({ YAxisColumn: yAxisKeys, GroupColumn: groupColumn });
  yKeys.forEach(add);

  if (chartFallback) {
    add(chartFallback.XAxisColumn);
    add(chartFallback.ZAxisColumn);
    add(chartFallback.GroupColumn);
    parseYAxisKeysFromChart(chartFallback, { excludeGroup: false }).forEach(add);
  }

  if (groupColumn) {
    add(groupColumn);
  }

  return [...columns];
}

/**
 * Merge chart metadata after save/update for local storage and plot rendering.
 */
/**
 * Find column metadata by internal name (exact, then case-insensitive, then partial).
 */
export function findColumnMeta(columnKey, items = []) {
  if (!columnKey || !Array.isArray(items) || items.length === 0) return null;
  const key = String(columnKey).trim();
  let found = items.find((item) => item.name === key);
  if (!found) {
    found = items.find((item) => item.name?.toLowerCase() === key.toLowerCase());
  }
  if (!found) {
    found = items.find(
      (item) =>
        item.name?.toLowerCase().includes(key.toLowerCase()) ||
        key.toLowerCase().includes(item.name?.toLowerCase() || "")
    );
  }
  return found || null;
}

/**
 * Resolve human-readable label; prefer column metadata, then stored label, then key.
 */
export function resolveColumnDisplayName(columnKey, items = [], storedDisplayName = "") {
  const meta = findColumnMeta(columnKey, items);
  if (meta?.displayName) return meta.displayName;
  const key = String(columnKey || "").trim();
  const stored = storedDisplayName != null ? String(storedDisplayName).trim() : "";
  if (stored && stored !== key) return stored;
  return key || stored;
}

/**
 * Resolve Y-axis display names aligned with yAxisKeys order.
 */
export function resolveYAxisDisplayNames(yAxisKeys, items = [], storedDisplayNames = []) {
  const keys = Array.isArray(yAxisKeys) ? yAxisKeys : [];
  const stored = Array.isArray(storedDisplayNames) ? storedDisplayNames : [];
  return keys.map((key, index) =>
    resolveColumnDisplayName(key, items, stored[index] || "")
  );
}

/** Match dashboard display-name lookup (API metadata uses capitalized field names). */
export function normalizeFieldNameForDisplayLookup(fieldName) {
  if (!fieldName || typeof fieldName !== "string") return fieldName;
  return fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
}

/**
 * Resolve axis labels from loaded column catalog + any labels already on the chart.
 */
export function buildAxisDisplayNamesFromChart(chart, catalog = {}) {
  if (!chart) {
    return {
      xAxisDisplayName: "",
      yAxisDisplayNames: [],
      zAxisDisplayName: "",
      colorGroupDisplayName: "",
    };
  }
  const yKeys = parseYAxisKeysFromChart(chart);
  const storedY = chart.yAxisDisplayNames || chart.processedYAxisDisplayNames || [];
  return {
    xAxisDisplayName: resolveColumnDisplayName(
      chart.XAxisColumn,
      catalog.xAxisItems || [],
      chart.xAxisDisplayName || chart.XAxisDisplayName || ""
    ),
    yAxisDisplayNames: resolveYAxisDisplayNames(
      yKeys,
      catalog.yAxisItems || [],
      storedY
    ),
    zAxisDisplayName: resolveColumnDisplayName(
      chart.ZAxisColumn,
      catalog.zAxisItems || [],
      chart.zAxisDisplayName || chart.ZAxisDisplayName || ""
    ),
    colorGroupDisplayName: resolveColumnDisplayName(
      chart.GroupColumn,
      catalog.colorGroupItems || [],
      chart.colorGroupDisplayName || chart.GroupColumnDisplayName || ""
    ),
  };
}

/**
 * Fetch human-readable axis labels from dashboard metadata cache (same as dashboard charts).
 */
export async function enrichChartAxisDisplayNames(chart) {
  const empty = {
    xAxisDisplayName: chart?.xAxisDisplayName || chart?.XAxisDisplayName || "",
    yAxisDisplayNames: Array.isArray(chart?.yAxisDisplayNames)
      ? [...chart.yAxisDisplayNames]
      : [],
    zAxisDisplayName: chart?.zAxisDisplayName || chart?.ZAxisDisplayName || "",
    colorGroupDisplayName:
      chart?.colorGroupDisplayName || chart?.GroupColumnDisplayName || "",
  };
  if (!chart?.TableType) {
    return empty;
  }

  const { getDisplayNameDashboard } = await import("./cache");

  const resolveField = async (fieldName, stored = "") => {
    if (!fieldName) return stored || "";
    if (stored && stored !== fieldName) return stored;
    const candidates = [
      fieldName,
      normalizeFieldNameForDisplayLookup(fieldName),
    ];
    for (const candidate of candidates) {
      try {
        const result = await getDisplayNameDashboard(chart.TableType, candidate);
        if (result?.displayName && result.displayName !== fieldName) {
          return result.displayName;
        }
      } catch (e) {
        /* try next candidate */
      }
    }
    return stored && stored !== fieldName ? stored : fieldName;
  };

  const yKeys = parseYAxisKeysFromChart(chart);
  const storedY = chart.yAxisDisplayNames || chart.processedYAxisDisplayNames || [];
  const yAxisDisplayNames = await Promise.all(
    yKeys.map((key, index) => resolveField(key, storedY[index] || ""))
  );

  return {
    xAxisDisplayName: await resolveField(
      chart.XAxisColumn,
      chart.xAxisDisplayName || chart.XAxisDisplayName || ""
    ),
    yAxisDisplayNames,
    zAxisDisplayName: await resolveField(
      chart.ZAxisColumn,
      chart.zAxisDisplayName || chart.ZAxisDisplayName || ""
    ),
    colorGroupDisplayName: await resolveField(
      chart.GroupColumn,
      chart.colorGroupDisplayName || chart.GroupColumnDisplayName || ""
    ),
  };
}

export function mergeChartMetadataAfterUpdate(existing, apiChart, sentPayload) {
  const groupColumn =
    sentPayload?.GroupColumn ?? apiChart?.GroupColumn ?? existing?.GroupColumn ?? "";
  const yAxisKeys = parseYAxisKeysFromChart(
    {
      YAxisColumn: sentPayload?.YAxisColumn,
      YAxisColumns: sentPayload?.YAxisColumns,
      GroupColumn: groupColumn,
    },
    { excludeGroup: true, groupColumn }
  );
  const yAxisColumns = buildYAxisColumnsArray(yAxisKeys, groupColumn);

  return {
    ...existing,
    ...(apiChart || {}),
    Name: apiChart?.Name || apiChart?.name || sentPayload?.name || existing?.Name || "",
    Filter: sentPayload?.Filter ?? apiChart?.Filter ?? existing?.Filter ?? {},
    XAxisColumn: sentPayload?.XAxisColumn ?? apiChart?.XAxisColumn ?? existing?.XAxisColumn ?? "",
    YAxisColumn: buildYAxisColumnString(yAxisKeys, groupColumn),
    YAxisColumns: yAxisColumns,
    GroupColumn: groupColumn,
    ZAxisColumn: sentPayload?.ZAxisColumn ?? apiChart?.ZAxisColumn ?? existing?.ZAxisColumn ?? "",
    ChartType: sentPayload?.ChartType ?? apiChart?.ChartType ?? existing?.ChartType ?? "",
    TableType: sentPayload?.TableType ?? apiChart?.TableType ?? existing?.TableType ?? "",
    XAxisScale: sentPayload?.XAxisScale ?? apiChart?.XAxisScale ?? existing?.XAxisScale ?? "linear",
    YAxisScale: sentPayload?.YAxisScale ?? apiChart?.YAxisScale ?? existing?.YAxisScale ?? "linear",
  };
}

/** Remove all "%" from chart/CSV labels (e.g. Mass % (wt%) → Mass (wt)). */
export function stripRedundantPercentFromLabel(text) {
  if (!text || typeof text !== "string") return text;
  return text.replace(/%/g, "").replace(/\s+/g, " ").trim();
}

/** Plain axis label for CSV/exports: "Name (unit)". */
export function formatChartAxisLabelPlain(name, unit) {
  if (!name) return "";
  if (unit && unit !== "-") {
    return `${name} (${unit})`;
  }
  return name;
}

/** Two-line HTML label for Plotly y-axis titles (name + unit on separate lines). */
export function formatChartAxisLabelTwoLines(name, unit) {
  if (!name) return "";
  if (unit && unit !== "-") {
    return `${name}<br>(${unit})`;
  }
  return name;
}

/** Vertical y-axis title: name and unit on separate lines, anchored at plot bottom. */
export function buildVerticalYAxisTitle(name, unit, options = {}) {
  const text = formatChartAxisLabelTwoLines(name, unit);
  if (!text) return { text: "" };

  return {
    text,
    standoff: options.standoff ?? 20,
    y: options.y ?? 0,
    yref: options.yref ?? "paper",
    yanchor: options.yanchor ?? "bottom",
    font: {
      size: options.fontSize ?? 11,
      color: options.color ?? "#374151",
      ...(options.font || {}),
    },
  };
}
